import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PayerAxiosInstance } from "../../../Network/Payer";
import { useToast } from "../../../hooks/use-toast";
import { Toaster } from "../../../components/ui/toaster";
import styles from "./EmployeeManagement.module.css";
import { Employee, FormState, EMPTY_FORM, ROLE_OPTIONS, getInitials, getApiError } from "../../../interface/Employee";

const useCompanyGuard = () => {
  const navigate = useNavigate();
  const coStr     = sessionStorage.getItem("selectedCompany");
  const company   = coStr ? (() => { try { return JSON.parse(coStr); } catch { return null; } })() : null;
  const companyId = company?.id;
  const INVALID   = new Set(["", "undefined", "null", "__no_id__"]);
  const isValid   = companyId && !INVALID.has(String(companyId));
  return { company, companyId: isValid ? String(companyId) : null, navigate };
};

export default function EmployeeManagement() {
  const { company, companyId, navigate } = useCompanyGuard();

  const [employees, setEmployees]       = useState<Employee[]>([]);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState("");
  const [filterRole, setFilterRole]     = useState<string>("all");
  const [filterActive, setFilterActive] = useState<string>("all");

  const [dialog, setDialog]         = useState<"add" | "edit" | "delete" | "reset" | null>(null);
  const [targetEmp, setTargetEmp]   = useState<Employee | null>(null);
  const [form, setForm]             = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [resetPass, setResetPass]   = useState("");

  const { toast } = useToast();

  useEffect(() => { fetchEmployees(); }, [companyId]);

  /* ── filtered ── */
  const filtered = useMemo(() => employees.filter((e) => {
    const q          = search.toLowerCase();
    const matchSearch = !q || e.name.toLowerCase().includes(q) || e.username.toLowerCase().includes(q);
    const matchRole   = filterRole   === "all" || e.role === filterRole;
    const matchActive =
      filterActive === "all" ||
      (filterActive === "active"   &&  e.isActive) ||
      (filterActive === "inactive" && !e.isActive);
    return matchSearch && matchRole && matchActive;
  }), [employees, search, filterRole, filterActive]);

  /* ── No company selected → show blocker ── */
  if (!companyId) {
    return (
      <div className={styles.root} data-testid="EmployeeManagement-no-company-root">
        <div className={styles.noCompanyWrap}>
          <div className={styles.noCompanyIcon}>🏢</div>
          <h2 className={styles.noCompanyTitle}>No Company Selected</h2>
          <p className={styles.noCompanySub}>Please select a company first before managing Accounts.</p>
          <button data-testid="EmployeeManagement-btn-select-company" className={styles.btnPrimary} onClick={() => navigate("/admin/companies")}>Select Company</button>
        </div>
      </div>
    );
  }

  /* ── fetch ── */
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await PayerAxiosInstance.get(`company/${companyId}/employee`, {
        params: { page: 1, limit: 100 },
      });
      setEmployees(res.data?.data?.items ?? []);
    } catch (e) {
      toast({ title: "Error", description: getApiError(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  /* ── dialog helpers ── */
  const openAdd     = () => { setForm(EMPTY_FORM); setTargetEmp(null); setDialog("add"); };
  const openEdit    = (emp: Employee) => { setForm({ name: emp.name, username: emp.username, password: "", role: emp.role }); setTargetEmp(emp); setDialog("edit"); };
  const openDelete  = (emp: Employee) => { setTargetEmp(emp); setDialog("delete"); };
  const openReset   = (emp: Employee) => { setTargetEmp(emp); setResetPass(""); setDialog("reset"); };
  const closeDialog = () => { setDialog(null); setTargetEmp(null); setForm(EMPTY_FORM); };

  /* ── CRUD ── */
  const handleAdd = async () => {
    if (!form.name || !form.username || !form.password) {
      toast({ title: "Validation", description: "All fields are required", variant: "destructive" }); return;
    }
    if (form.password.length < 4) {
      toast({ title: "Validation", description: "Password must be at least 4 characters", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      await PayerAxiosInstance.post(`company/${companyId}/employee`, form);
      toast({ title: "Success", description: "Account added successfully", variant: "success" });
      closeDialog(); fetchEmployees();
    } catch (e) {
      toast({ title: "Error", description: getApiError(e), variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!targetEmp || !form.name || !form.username) {
      toast({ title: "Validation", description: "Name and username are required", variant: "destructive" }); return;
    }
    if (form.password && form.password.length < 4) {
      toast({ title: "Validation", description: "Password must be at least 4 characters", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const payload: any = { name: form.name, username: form.username, role: form.role, isActive: targetEmp.isActive };
      if (form.password) payload.password = form.password;
      await PayerAxiosInstance.put(`company/${companyId}/employee/${targetEmp.id}`, payload);
      toast({ title: "Success", description: "Account updated", variant: "success" });
      closeDialog(); fetchEmployees();
    } catch (e) {
      toast({ title: "Error", description: getApiError(e), variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!targetEmp) return;

    if (targetEmp.role === "Super Super Admin") {
      const superSuperAdmins = employees.filter((e) => e.role === "Super Super Admin");
      if (superSuperAdmins.length <= 1) {
        toast({
          title: "Action Not Allowed",
          description: "Cannot delete the last Super Super Admin. Add another one first.",
          variant: "destructive",
        });
        closeDialog();
        return;
      }
    }

    setSubmitting(true);
    try {
      await PayerAxiosInstance.delete(`company/${companyId}/employee/${targetEmp.id}`);
      toast({ title: "Deleted", description: `${targetEmp.name} removed`, variant: "success" });
      closeDialog(); fetchEmployees();
    } catch (e) {
      toast({ title: "Error", description: getApiError(e), variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const handleToggleActive = async (emp: Employee) => {
    const nextActiveStatus = !emp.isActive;
    
    try {
      await PayerAxiosInstance.put(`company/${companyId}/employee/${emp.id}`, {
        name: emp.name,
        username: emp.username,
        role: emp.role,
        isActive: nextActiveStatus,
      });
      setEmployees((prev) =>
        prev.map((e) => (e.id === emp.id ? { ...e, isActive: nextActiveStatus } : e))
      );
      toast({ title: "Updated", description: `${emp.name} is now ${nextActiveStatus ? "active" : "inactive"}`, variant: "success" });
    } catch (e) {
      toast({ title: "Error", description: getApiError(e), variant: "destructive" });
    }
  };

  const handleResetPassword = async () => {
    if (!targetEmp || !resetPass) {
      toast({ title: "Validation", description: "New password is required", variant: "destructive" }); return;
    }
    if (resetPass.length < 4) {
      toast({ title: "Validation", description: "Password must be at least 4 characters", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      await PayerAxiosInstance.put(`company/${companyId}/employee/${targetEmp.id}`, {
        name: targetEmp.name, username: targetEmp.username,
        role: targetEmp.role, active: targetEmp.isActive, password: resetPass,
      });
      toast({ title: "Password Reset", description: "Password updated successfully", variant: "success" });
      closeDialog();
    } catch (e) {
      toast({ title: "Error", description: getApiError(e), variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  /* ── Role Badge ── */
  const RoleBadge = ({ role }: { role: string }) => (
    <span className={`${styles.roleBadge} ${
      role === "Super Super Admin" ? styles.roleSuperSuper :
      role === "Viewer"            ? styles.roleViewer :
                                     styles.roleSuper
    }`} data-testid={`EmployeeManagement-role-badge-${role.toLowerCase().replace(/\s+/g, '-')}`}>
      {role === "Super Super Admin" ? "⭐ Super Super Admin" :
       role === "Viewer"            ? "👁️ Viewer" :
                                      "Super Admin"}
    </span>
  );

  /* ── Stats ── */
  const totalActive   = employees.filter((e) => e.isActive).length;
  const totalInactive = employees.length - totalActive;
  const totalViewers  = employees.filter((e) => e.role === "Viewer").length;
  const totalSuperSuper = employees.filter((e) => e.role === "Super Super Admin").length;

  /* ── RENDER ── */
  return (
    <div className={styles.root} data-testid="EmployeeManagement-page">
      <Toaster />

      {/* Header */}
      <div className={styles.pageHeader} data-testid="EmployeeManagement-page-header">
        <div className={styles.pageHeaderLeft}>
          <div className={styles.pageIcon}>👥</div>
          <div>
            <h1 className={styles.pageTitle} data-testid="EmployeeManagement-page-title">Account Management</h1>
            <p className={styles.pageSub}>
              <span className={styles.companyBadge} data-testid="EmployeeManagement-company-name">{company?.name}</span>
              <span className={styles.companyId} data-testid="EmployeeManagement-company-id">ID: {companyId.slice(0, 8)}…</span>
            </p>
          </div>
        </div>
        <button data-testid="EmployeeManagement-btn-add-account" className={styles.btnPrimary} onClick={openAdd}>
          <span>＋</span> Add Account
        </button>
      </div>

      {/* Stats */}
      <div className={styles.statsRow} data-testid="EmployeeManagement-stats-row">
        <div className={styles.statCard} data-testid="EmployeeManagement-stat-total">
          <div className={styles.statNum}>{employees.length}</div>
          <div className={styles.statLabel}>Total</div>
        </div>
        <div className={`${styles.statCard} ${styles.statGreen}`} data-testid="EmployeeManagement-stat-active">
          <div className={styles.statNum}>{totalActive}</div>
          <div className={styles.statLabel}>Active</div>
        </div>
        <div className={`${styles.statCard} ${styles.statRed}`} data-testid="EmployeeManagement-stat-inactive">
          <div className={styles.statNum}>{totalInactive}</div>
          <div className={styles.statLabel}>Inactive</div>
        </div>
        <div className={`${styles.statCard} ${styles.statPurple}`} data-testid="EmployeeManagement-stat-super">
          <div className={styles.statNum}>{totalSuperSuper}</div>
          <div className={styles.statLabel}>Super Super Admin</div>
        </div>
        <div className={`${styles.statCard} ${styles.statBlue}`} data-testid="EmployeeManagement-stat-viewers">
          <div className={styles.statNum}>{totalViewers}</div>
          <div className={styles.statLabel}>Viewers</div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersRow} data-testid="EmployeeManagement-filters-row">
        <div className={styles.searchWrap}>
          <svg className={styles.searchIco} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            data-testid="EmployeeManagement-search-input"
            className={styles.searchInput}
            placeholder="Search by name or username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select data-testid="EmployeeManagement-filter-role" className={styles.filterSelect} value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="Super Admin">Super Admin</option>
          <option value="Super Super Admin">Super Super Admin</option>
          <option value="Viewer">Viewer</option>
        </select>
        <select data-testid="EmployeeManagement-filter-active" className={styles.filterSelect} value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className={styles.tableWrap} data-testid="EmployeeManagement-table-wrap">
        {loading ? (
          <div className={styles.loadingWrap} data-testid="EmployeeManagement-loading-state"><div className={styles.spinner} /><p>Loading Accounts…</p></div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyWrap} data-testid="EmployeeManagement-empty-state">
            <div className={styles.emptyIcon}>👤</div>
            <p className={styles.emptyTitle}>No Accounts found</p>
            <p className={styles.emptySub}>Add your first Account using the button above</p>
          </div>
        ) : (
          <table className={styles.table} data-testid="EmployeeManagement-table">
            <thead>
              <tr>
                <th>Account</th><th>Username</th><th>Role</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp) => (
                <tr key={emp.id} className={!emp.isActive ? styles.rowInactive : ""} data-testid={`EmployeeManagement-row-${emp.id}`}>
                  <td>
                    <div className={styles.empCell}>
                      <div className={`${styles.avatar} ${
                        emp.role === "Super Super Admin" ? styles.avatarPurple :
                        emp.role === "Viewer"            ? styles.avatarBlue :
                                                           styles.avatarBlue
                      }`}>
                        {getInitials(emp.name)}
                      </div>
                      <span className={styles.empName} data-testid={`EmployeeManagement-emp-name-${emp.id}`}>{emp.name}</span>
                    </div>
                  </td>
                  <td className={styles.usernameCell} data-testid={`EmployeeManagement-emp-username-${emp.id}`}>@{emp.username}</td>
                  <td><RoleBadge role={emp.role} /></td>
                  <td>
                    <button
                      data-testid={`EmployeeManagement-toggle-status-${emp.id}`}
                      className={`${styles.toggle} ${emp.isActive ? styles.toggleOn : styles.toggleOff}`}
                      onClick={() => handleToggleActive(emp)}
                    >
                      <span className={styles.toggleThumb} />
                    </button>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button data-testid={`EmployeeManagement-btn-edit-${emp.id}`} className={styles.btnEdit}   onClick={() => openEdit(emp)}   title="Edit">✏️</button>
                      <button data-testid={`EmployeeManagement-btn-reset-${emp.id}`} className={styles.btnReset}  onClick={() => openReset(emp)}  title="Reset Password">🔑</button>
                      <button data-testid={`EmployeeManagement-btn-delete-${emp.id}`} className={styles.btnDelete} onClick={() => openDelete(emp)} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Dialog */}
      {(dialog === "add" || dialog === "edit") && (
        <div className={styles.overlay} onClick={closeDialog} data-testid="EmployeeManagement-form-dialog-overlay">
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} data-testid="EmployeeManagement-form-dialog-content">
            <div className={styles.modalHeader}>
              <h2 data-testid="EmployeeManagement-form-dialog-title">{dialog === "add" ? "Add New Account" : "Edit Account"}</h2>
              <button data-testid="EmployeeManagement-form-dialog-btn-close" className={styles.closeBtn} onClick={closeDialog}>✕</button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup} data-testid="EmployeeManagement-form-group-name">
                <label>Full Name *</label>
                <input
                  data-testid="EmployeeManagement-form-input-name"
                  className={styles.input}
                  placeholder="Enter full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup} data-testid="EmployeeManagement-form-group-username">
                <label>Username *</label>
                <input
                  data-testid="EmployeeManagement-form-input-username"
                  className={styles.input}
                  placeholder="Enter username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
              </div>
              <div className={styles.formGroup} data-testid="EmployeeManagement-form-group-password">
                <label>{dialog === "add" ? "Password *" : "New Password (leave blank to keep)"}</label>
                <input
                  data-testid="EmployeeManagement-form-input-password"
                  className={styles.input}
                  type="password"
                  placeholder="Min 4 characters"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div className={styles.formGroup} data-testid="EmployeeManagement-form-group-role">
                <label>Role *</label>
                <div className={styles.roleOptions} data-testid="EmployeeManagement-role-options-container">
                  {ROLE_OPTIONS.map((r) => (
                    <label key={r} className={`${styles.roleOption} ${form.role === r ? styles.roleOptionActive : ""}`} data-testid={`EmployeeManagement-role-option-${r.toLowerCase().replace(/\s+/g, '-')}`}>
                      <input
                        data-testid={`EmployeeManagement-radio-role-${r.toLowerCase().replace(/\s+/g, '-')}`}
                        type="radio"
                        name="role"
                        value={r}
                        checked={form.role === r}
                        onChange={() => setForm({ ...form, role: r })}
                      />
                      <span>
                        {r === "Super Super Admin" ? "⭐ " : r === "Viewer" ? "👁️ " : ""}
                        {r}
                      </span>
                      {r === "Viewer" && (
                        <span className={styles.roleHint}>View only</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter} data-testid="EmployeeManagement-form-dialog-footer">
              <button data-testid="EmployeeManagement-form-btn-cancel" className={styles.btnSecondary} onClick={closeDialog}>Cancel</button>
              <button
                data-testid="EmployeeManagement-form-btn-submit"
                className={styles.btnPrimary}
                onClick={dialog === "add" ? handleAdd : handleEdit}
                disabled={submitting}
              >
                {submitting ? "Saving…" : dialog === "add" ? "Add Account" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {dialog === "delete" && targetEmp && (
        <div className={styles.overlay} onClick={closeDialog} data-testid="EmployeeManagement-delete-dialog-overlay">
          <div className={`${styles.modal} ${styles.modalSm}`} onClick={(e) => e.stopPropagation()} data-testid="EmployeeManagement-delete-dialog-content">
            <div className={styles.modalHeader}>
              <h2 data-testid="EmployeeManagement-delete-dialog-title">Delete Account</h2>
              <button data-testid="EmployeeManagement-delete-dialog-btn-close" className={styles.closeBtn} onClick={closeDialog}>✕</button>
            </div>
            <div className={styles.modalBody} data-testid="EmployeeManagement-delete-dialog-body">
              <div className={styles.deleteWarning}>
                <div className={styles.deleteIcon}>🗑️</div>
                <p>Are you sure you want to permanently delete <strong data-testid="EmployeeManagement-delete-target-name">{targetEmp.name}</strong>?</p>
                <p className={styles.deleteNote}>This action cannot be undone.</p>
              </div>
            </div>
            <div className={styles.modalFooter} data-testid="EmployeeManagement-delete-dialog-footer">
              <button data-testid="EmployeeManagement-delete-btn-cancel" className={styles.btnSecondary} onClick={closeDialog}>Cancel</button>
              <button data-testid="EmployeeManagement-delete-btn-confirm" className={styles.btnDanger} onClick={handleDelete} disabled={submitting}>
                {submitting ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Dialog */}
      {dialog === "reset" && targetEmp && (
        <div className={styles.overlay} onClick={closeDialog} data-testid="EmployeeManagement-reset-dialog-overlay">
          <div className={`${styles.modal} ${styles.modalSm}`} onClick={(e) => e.stopPropagation()} data-testid="EmployeeManagement-reset-dialog-content">
            <div className={styles.modalHeader}>
              <h2 data-testid="EmployeeManagement-reset-dialog-title">Reset Password</h2>
              <button data-testid="EmployeeManagement-reset-dialog-btn-close" className={styles.closeBtn} onClick={closeDialog}>✕</button>
            </div>
            <div className={styles.modalBody} data-testid="EmployeeManagement-reset-dialog-body">
              <p className={styles.resetSub}>Setting new password for <strong data-testid="EmployeeManagement-reset-target-name">{targetEmp.name}</strong></p>
              <div className={styles.formGroup} data-testid="EmployeeManagement-reset-form-group">
                <label>New Password *</label>
                <input
                  data-testid="EmployeeManagement-reset-input-password"
                  className={styles.input}
                  type="password"
                  placeholder="Min 4 characters"
                  value={resetPass}
                  onChange={(e) => setResetPass(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.modalFooter} data-testid="EmployeeManagement-reset-dialog-footer">
              <button data-testid="EmployeeManagement-reset-btn-cancel" className={styles.btnSecondary} onClick={closeDialog}>Cancel</button>
              <button data-testid="EmployeeManagement-reset-btn-confirm" className={styles.btnPrimary} onClick={handleResetPassword} disabled={submitting}>
                {submitting ? "Saving…" : "Reset Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
